import axios, { AxiosInstance } from 'axios';
import { API_BASE_URL, STORAGE_KEYS } from '../constants';
import type {
  AgentCreditPurchaseData,
  AgentWalletLedgerEntry,
  AgentDashboardData,
  AgentLoginResponse,
  AgentShop,
  AgentTransactionsPagination,
  AgentPlayer,
  ManagedShopUsdLedgerData,
  SuperManagedAgent,
  SuperManagedPlayer,
  SuperManagedShop,
  ZeroxStaticWallet,
} from '../types';

function createClient(): AxiosInstance {
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: 15000,
    headers: { 'Content-Type': 'application/json' },
  });

  client.interceptors.request.use((config) => {
    if (typeof window === 'undefined') return config;
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (token) {
      const h = config.headers;
      if (h && typeof (h as { set?: (k: string, v: string) => void }).set === 'function') {
        (h as { set: (k: string, v: string) => void }).set('Authorization', `Bearer ${token}`);
      } else {
        config.headers = config.headers ?? {};
        (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
      }
    }
    return config;
  });

  client.interceptors.response.use(
    (res) => res,
    (error) => {
      if (error.response?.status === 401) {
        window.dispatchEvent(new Event('auth:unauthorized'));
      }
      return Promise.reject(error);
    }
  );

  return client;
}

const client = createClient();

export async function getProviderFeeRate(params: {
  provider: string;
  operation: 'deposit' | 'withdrawal' | 'transfer';
  methodId?: string;
}): Promise<{ success: boolean; data?: { feeRate: number; matchedMethodId?: string | null }; message?: string }> {
  const { data } = await client.get('/payment/provider-fee-rate', {
    params: {
      provider: params.provider,
      operation: params.operation,
      methodId: params.methodId ?? 'all',
    },
  });
  return data;
}

export async function fetchProviderFeeConfigs(): Promise<{
  success: boolean;
  data?: {
    items: Array<{
      id: string;
      provider: string;
      operation: string;
      methodId: string;
      paymentGateFeeRate?: number;
      systemFeeRate?: number;
      feeRate: number;
      networkFeeType?: 'none' | 'percent' | 'fixed';
      networkFeeRate?: number;
      networkFeeAmount?: number;
      isEnabled?: boolean;
    }>;
    providers?: string[];
    supportedCatalog?: Record<
      string,
      { methods?: Array<{ methodId?: string; id?: string; name?: string; ticker?: string; currency?: string }> }
    >;
  };
  message?: string;
}> {
  const { data } = await client.get('/super-agent/provider-fees');
  return data;
}

export async function loginWithUsernameOrEmail(
  usernameOrEmail: string,
  password: string,
  captchaToken?: string
): Promise<AgentLoginResponse> {
  const loginId = usernameOrEmail.trim();
  const { data } = await client.post<AgentLoginResponse>('/super-agent-auth/login', {
    username: loginId,
    email: loginId,
    password,
    ...(captchaToken ? { captchaToken } : {}),
  });
  return data;
}

export async function verifyPortalLoginOtp(
  pendingToken: string,
  otp: string
): Promise<AgentLoginResponse> {
  const { data } = await client.post<AgentLoginResponse>('/super-agent-auth/verify-otp', {
    pendingToken,
    otp,
  });
  return data;
}

export async function patchPortalTwoFactor(payload: {
  two_factor_enabled: boolean;
  two_factor_channel?: 'email' | 'phone';
}): Promise<{
  success: boolean;
  message?: string;
  data?: {
    two_factor_enabled: boolean;
    two_factor_channel?: 'email' | 'phone' | null;
    email_verified?: boolean;
    phone_verified?: boolean;
  };
}> {
  const { data } = await client.patch('/super-agent/settings/two-factor', payload);
  return data;
}

export type ForgotPasswordRequestResponse = {
  success: boolean;
  message?: string;
  data?: {
    token: string;
    channel: 'email' | 'phone';
    expiresInSeconds: number;
    resend_count?: number;
    next_resend_at?: string;
    expires_at?: string;
  };
};

export async function requestForgotPasswordOtp(contact: string): Promise<ForgotPasswordRequestResponse> {
  const { data } = await client.post('/super-agent-auth/forgot-password/request-otp', { contact });
  return data;
}

export async function verifyForgotPasswordOtp(
  token: string,
  otp: string
): Promise<{ success: boolean; message?: string; data?: { token: string } }> {
  const { data } = await client.post('/super-agent-auth/forgot-password/verify-otp', { token, otp });
  return data;
}

export async function confirmForgotPasswordReset(payload: {
  token: string;
  otp: string;
  newPassword: string;
  confirmPassword: string;
}): Promise<{ success: boolean; message?: string }> {
  const { data } = await client.post('/super-agent-auth/forgot-password/confirm', payload);
  return data;
}

export async function fetchManagedAgents(): Promise<{
  success: boolean;
  data?: SuperManagedAgent[];
  message?: string;
}> {
  const { data } = await client.get('/super-agent/agents');
  return data;
}

export async function fetchManagedAgent(agentId: string): Promise<{
  success: boolean;
  data?: { agent: SuperManagedAgent; shops?: SuperManagedShop[] };
  message?: string;
}> {
  const { data } = await client.get(`/super-agent/agents/${encodeURIComponent(agentId)}`);
  return data;
}

export async function createManagedAgent(payload: {
  name: string;
  username: string;
  email: string;
  phone?: string;
  password: string;
  cost_rate: number;
  can_create_shop_popup?: boolean;
  can_manage_shop_popup_permission?: boolean;
  default_shop_can_create_player_popup?: boolean;
}): Promise<{ success: boolean; data?: Record<string, unknown>; message?: string }> {
  const { data } = await client.post('/super-agent/agents', payload);
  return data;
}

export async function suspendManagedAgent(agentId: string): Promise<{ success: boolean; message?: string }> {
  const { data } = await client.patch(`/super-agent/agents/${agentId}/suspend`);
  return data;
}

export async function unsuspendManagedAgent(agentId: string): Promise<{ success: boolean; message?: string }> {
  const { data } = await client.patch(`/super-agent/agents/${agentId}/unsuspend`);
  return data;
}

/** PATCH /super-agent/agents/:id/rates — `cost_rate` is decimal 0–1 (e.g. 0.25 = 25%). */
export async function updateManagedAgentRate(
  agentId: string,
  cost_rate: number
): Promise<{ success: boolean; data?: SuperManagedAgent; message?: string }> {
  const { data } = await client.patch(`/super-agent/agents/${agentId}/rates`, { cost_rate });
  return data;
}

export async function updateManagedAgentPassword(
  agentId: string,
  password: string
): Promise<{ success: boolean; message?: string }> {
  const { data } = await client.patch(`/super-agent/agents/${agentId}/password`, { password });
  return data;
}

export async function updateManagedAgentName(
  agentId: string,
  name: string
): Promise<{ success: boolean; data?: SuperManagedAgent; message?: string }> {
  const { data } = await client.patch(`/super-agent/agents/${agentId}/name`, { name });
  return data;
}

export async function updateManagedAgentPopupPermissions(
  agentId: string,
  body: {
    can_create_shop_popup?: boolean;
    can_manage_shop_popup_permission?: boolean;
    default_shop_can_create_player_popup?: boolean;
  }
): Promise<{ success: boolean; data?: SuperManagedAgent; message?: string }> {
  const { data } = await client.patch(`/super-agent/agents/${agentId}/popup-permissions`, body);
  return data;
}

export async function fetchManagedShops(): Promise<{
  success: boolean;
  data?: SuperManagedShop[];
  message?: string;
}> {
  const { data } = await client.get('/super-agent/shops');
  return data;
}

export async function fetchManagedPlayers(params?: {
  shop_id?: string;
  q?: string;
}): Promise<{
  success: boolean;
  data?: SuperManagedPlayer[];
  message?: string;
}> {
  const { data } = await client.get('/super-agent/players', { params });
  return data;
}

export async function moveManagedShop(
  shopId: string,
  targetAgentId: string
): Promise<{ success: boolean; data?: SuperManagedShop; message?: string }> {
  const { data } = await client.patch(`/super-agent/shops/${shopId}/move`, {
    target_agent_id: targetAgentId
  });
  return data;
}

export async function moveManagedPlayer(
  playerId: string,
  targetShopId: string
): Promise<{ success: boolean; data?: SuperManagedPlayer; message?: string }> {
  const { data } = await client.patch(`/super-agent/players/${playerId}/move`, {
    target_shop_id: targetShopId
  });
  return data;
}

export async function fetchDashboard(): Promise<{
  success: boolean;
  data?: AgentDashboardData;
}> {
  const { data } = await client.get('/super-agent/dashboard');
  return data;
}

export type DashboardV2Period = 'today' | 'week' | 'month';

export type DashboardKpi = { value: number; previous?: number; deltaPct?: number | null };

export type SuperAgentDashboardV2Envelope = {
  period: DashboardV2Period;
  range_start: string;
  range_end: string;
  timezone: string;
  data_as_of: string;
  comparison: { range_start: string; range_end: string };
};

export type SuperAgentDashboardOverviewData = SuperAgentDashboardV2Envelope & {
  kpis: {
    netPosition: DashboardKpi;
    earnedTotal: DashboardKpi;
    spentTotal: DashboardKpi;
    creditBalance: DashboardKpi;
    usdBalanceNet: DashboardKpi;
    playerMoneyIn: DashboardKpi;
    onlineDepositsCount: DashboardKpi;
    onlineWithdrawalsCount: DashboardKpi;
    onlineDepositsNetUsd: DashboardKpi;
    onlineWithdrawalsGrossUsd: DashboardKpi;
  };
  extras: {
    agentsCount: number;
    shopsCount: number;
    roadblocksBadgeCount: number;
    playerMoneyIn: number;
    cashSettlement: {
      expected: number;
      received: number;
      uncollected: number;
    };
  };
  charts: {
    dailyPlayerFlow: Array<{ date: string; usdIn: number; payoutsOut: number; net: number }>;
    topAgents: Array<{
      id: string;
      name: string;
      creditsPurchased: number;
      cashExpected: number;
      cashReceived: number;
    }>;
  };
};

export type SuperAgentDashboardFinancialData = SuperAgentDashboardV2Envelope & {
  kpis: {
    netPosition: DashboardKpi;
    earnedTotal: DashboardKpi;
    spentTotal: DashboardKpi;
    cashFromAgents: DashboardKpi;
    cdnSoldToAgents: DashboardKpi;
    convertsFromAgents: DashboardKpi;
    creditsPurchasedUsd: DashboardKpi;
    cashPaidUpstream: DashboardKpi;
    depositAttribution: DashboardKpi;
    counterMargin: DashboardKpi;
    payoutFunding: DashboardKpi;
    playerMoneyIn: DashboardKpi;
    onlineDepositsNetUsd: DashboardKpi;
    onlineDepositsCount: DashboardKpi;
    onlineWithdrawalsGrossUsd: DashboardKpi;
    onlineWithdrawalsCount: DashboardKpi;
    counterDepositsCashReceived: DashboardKpi;
    counterRedemptionsCashPaid: DashboardKpi;
    tierFundingUsd: DashboardKpi;
  };
  charts: {
    dailyNetPosition: Array<{ date: string; earned: number; spent: number; net: number }>;
    earnedMix: Array<{ name: string; value: number }>;
  };
};

export type SuperAgentDashboardAgentsData = SuperAgentDashboardV2Envelope & {
  agents: Array<{
    id: string;
    name: string;
    status: string;
    creditsPurchased: DashboardKpi;
    cashExpected: number;
    cashReceived: number;
    networkDeposits: number;
    networkWithdrawals: number;
  }>;
  summary: { creditsPurchased: number; cashExpected: number; cashReceived: number };
  pagination: { page: number; limit: number; total: number; totalPages: number };
};

export type SuperAgentDashboardNetworkData = SuperAgentDashboardV2Envelope & {
  kpis: {
    playerMoneyIn: DashboardKpi;
    onlineDepositsNetUsd: DashboardKpi;
    onlineDepositsCount: DashboardKpi;
    onlineWithdrawalsGrossUsd: DashboardKpi;
    onlineWithdrawalsCount: DashboardKpi;
    counterDepositsCashReceived: DashboardKpi;
    counterRedemptionsCashPaid: DashboardKpi;
    tierFundingUsd: DashboardKpi;
  };
};

export type SuperAgentDashboardRoadblocksData = SuperAgentDashboardV2Envelope & {
  roadblocks: Array<{
    id: string;
    label: string;
    count: number;
    amountUsd: number;
    link: string;
  }>;
  badgeCount: number;
};

async function fetchDashboardV2Tab<T>(
  tab: string,
  period: DashboardV2Period,
  refresh = false,
  extra?: Record<string, string | number>,
): Promise<T> {
  const { data } = await client.get(`/super-agent/dashboard/${tab}`, {
    params: { period, ...(refresh ? { refresh: 1 } : {}), ...extra },
  });
  if (!data?.success || !data.data) {
    throw new Error(data?.message || 'Failed to load dashboard');
  }
  return data.data as T;
}

export function fetchDashboardV2Overview(period: DashboardV2Period, refresh = false, tz?: string) {
  return fetchDashboardV2Tab<SuperAgentDashboardOverviewData>('overview', period, refresh, tz ? { tz } : undefined);
}

export function fetchDashboardV2Financial(period: DashboardV2Period, refresh = false, tz?: string) {
  return fetchDashboardV2Tab<SuperAgentDashboardFinancialData>('financial', period, refresh, tz ? { tz } : undefined);
}

export function fetchDashboardV2Agents(
  period: DashboardV2Period,
  params: { page?: number; tz?: string },
  refresh = false,
) {
  return fetchDashboardV2Tab<SuperAgentDashboardAgentsData>('agents', period, refresh, {
    page: params.page ?? 1,
    ...(params.tz ? { tz: params.tz } : {}),
  });
}

export function fetchDashboardV2Network(period: DashboardV2Period, refresh = false, tz?: string) {
  return fetchDashboardV2Tab<SuperAgentDashboardNetworkData>('network', period, refresh, tz ? { tz } : undefined);
}

export function fetchDashboardV2Roadblocks(period: DashboardV2Period, refresh = false, tz?: string) {
  return fetchDashboardV2Tab<SuperAgentDashboardRoadblocksData>('roadblocks', period, refresh, tz ? { tz } : undefined);
}

export type BalanceConversionResult = {
  credits_added: number;
  usd_deducted: number;
  cost_rate_applied: number;
  new_credit_balance: number;
  new_online_balance_net: number;
  upstream_usd_credited?: boolean;
};

export async function convertBalanceToCredits(
  amount: number,
  idempotencyKey?: string
): Promise<{ success: boolean; data?: BalanceConversionResult; message?: string }> {
  const body: { amount: number; idempotency_key?: string } = { amount };
  if (idempotencyKey && idempotencyKey.trim()) body.idempotency_key = idempotencyKey.trim();
  const { data } = await client.post('/super-agent/convert-balance-to-credits', body);
  return data;
}

export async function fetchSuperAgentOnlineBalanceLedger(params?: {
  currency?: string;
  type?: string;
  limit?: number;
  offset?: number;
  sort?: string;
  sort_dir?: string;
}): Promise<{ success: boolean; data?: any; message?: string }> {
  const { data } = await client.get('/super-agent/online-balance/ledger', { params });
  return data;
}

export async function fetchSuperAgentCreditTransactionById(
  id: string,
): Promise<{ success: boolean; data?: { item: AgentWalletLedgerEntry }; message?: string }> {
  const { data } = await client.get(`/super-agent/transactions/${id}`);
  return data;
}

export async function fetchSuperAgentUsdLedgerEntryById(
  id: string,
): Promise<{ success: boolean; data?: { item: Record<string, unknown> }; message?: string }> {
  const { data } = await client.get(`/super-agent/online-balance/ledger/${id}`);
  return data;
}

export type OnlineDebtPayoutNetwork = 'btc' | 'eth' | 'usdt_erc' | 'usdt_trc';

export type WithdrawalOtpEligibility = {
  canWithdraw: boolean;
  emailVerified: boolean;
  phoneVerified: boolean;
  maskedEmail?: string | null;
  maskedPhone?: string | null;
};

export async function fetchSuperAgentWithdrawalOtpEligibility(): Promise<{
  success: boolean;
  data?: WithdrawalOtpEligibility;
  message?: string;
}> {
  const { data } = await client.get('/super-agent/online-balance/withdrawals/otp/eligibility');
  return data;
}

export async function sendSuperAgentWithdrawalOtp(body: {
  channel: 'email' | 'phone';
}): Promise<{ success: boolean; data?: { next_resend_at?: string }; message?: string }> {
  const { data } = await client.post('/super-agent/online-balance/withdrawals/otp/send', body);
  return data;
}

export async function verifySuperAgentWithdrawalOtp(body: {
  channel: 'email' | 'phone';
  otp: string;
}): Promise<{ success: boolean; data?: { withdrawChallengeToken: string }; message?: string }> {
  const { data } = await client.post('/super-agent/online-balance/withdrawals/otp/verify', body);
  return data;
}

export async function createSuperAgentOnlineBalanceWithdrawal(body: {
  amountUsd: number;
  payoutNetwork: OnlineDebtPayoutNetwork;
  payoutWalletAddress: string;
  note?: string | null;
  idempotencyKey: string;
  withdrawChallengeToken: string;
}): Promise<{ success: boolean; data?: any; message?: string }> {
  const { data } = await client.post('/super-agent/online-balance/withdrawals', body);
  return data;
}

export type PortalWithdrawalLimitsSnapshot = {
  maxWithdrawalPerTransaction: number;
  maxWithdrawalPerDay: number;
  withdrawnTodayUsd: number;
  remainingTodayUsd: number;
  dailyLimitReached?: boolean;
};

export async function fetchSuperAgentPortalWithdrawalLimits(params?: {
  amountUsd?: number;
}): Promise<{
  success: boolean;
  data?: PortalWithdrawalLimitsSnapshot;
  message?: string;
}> {
  const { data } = await client.get('/super-agent/portal-withdrawal-limits', { params });
  return data;
}

export async function fetchSuperAgentOnlineBalanceWithdrawalFee(params: {
  payoutNetwork: OnlineDebtPayoutNetwork;
  amountUsd?: number;
}): Promise<{
  success: boolean;
  data?: {
    payoutNetwork: string;
    feeRate: number;
    systemFeeRate?: number;
    gatewayFeeRate?: number;
    paymentGateFeeMinUsd?: number;
    networkFeeType?: 'none' | 'percent' | 'fixed' | string;
    networkFeeRate?: number;
    networkFeeAmount?: number;
    matchedMethodId: string | null;
    feeConfigDisabled?: boolean;
    feeConfigDisabledScope?: string | null;
    feeConfigMessage?: string | null;
    tierFundingUsd?: number;
    gatewayPayoutUsd?: number;
    totalFeeUsd?: number;
    limits?: PortalWithdrawalLimitsSnapshot;
  };
  message?: string;
}> {
  const { data } = await client.get('/super-agent/online-balance/withdrawals/fee', { params });
  return data;
}

export async function sendSuperAgentVerificationOtp(payload?: {
  channel?: 'email' | 'phone';
}): Promise<{
  success: boolean;
  data?: { resend_count: number; next_resend_at: string; expires_at: string; channel: 'email' | 'phone' };
  message?: string;
}> {
  const { data } = await client.post('/super-agent/verify/send-otp', payload ?? {});
  return data;
}

export async function confirmSuperAgentVerificationOtp(payload: {
  channel: 'email' | 'phone';
  otp: string;
}): Promise<{
  success: boolean;
  data?: { superAgent: Record<string, unknown>; verified: boolean; channel: 'email' | 'phone' };
  message?: string;
}> {
  const { data } = await client.post('/super-agent/verify/confirm', payload);
  return data;
}

/** POST /super-agent/agents/:id/cash-credits — records cash sale + distributes credits (V1: cash_received locked = expected). */
export async function createManagedAgentCashCredits(
  agentId: string,
  payload: {
    credits: number;
    payment_method?: 'cash' | 'bank_transfer' | 'check' | 'other';
    received_at?: string;
    external_reference?: string;
    note?: string;
  }
): Promise<{ success: boolean; data?: unknown; message?: string }> {
  const { data } = await client.post(`/super-agent/agents/${agentId}/cash-credits`, payload);
  return data;
}

export async function getSuperAgentCashTransactions(params?: Record<string, unknown>): Promise<{
  success: boolean;
  data?: unknown;
  message?: string;
}> {
  const { data } = await client.get('/super-agent/cash-transactions', { params });
  return data;
}

export async function getSuperAgentCashTransactionDetail(
  id: string,
  createdAt?: string
): Promise<{ success: boolean; data?: unknown; message?: string }> {
  const { data } = await client.get(`/super-agent/cash-transactions/${encodeURIComponent(id)}`, {
    params: createdAt ? { created_at: createdAt } : undefined,
  });
  return data;
}

export async function exportSuperAgentCashTransactions(params?: Record<string, unknown>): Promise<{
  blob: Blob;
  filename: string;
}> {
  const response = await client.get<Blob>('/super-agent/cash-transactions/export', {
    params,
    responseType: 'blob',
  });
  const disposition = String(response.headers['content-disposition'] || '');
  const match = /filename="([^"]+)"/i.exec(disposition);
  const filename = match?.[1] || 'cash-transactions-export.csv';
  return { blob: response.data, filename };
}

/** POST /super-agent/agents/:id/credits/withdraw — debit managed agent wallet, credit super agent. */
export async function withdrawCreditsFromManagedAgent(
  agentId: string,
  amount: number,
  note?: string
): Promise<{ success: boolean; data?: unknown; message?: string }> {
  const { data } = await client.post(`/super-agent/agents/${agentId}/credits/withdraw`, {
    amount,
    ...(note != null && String(note).trim() !== '' ? { note: String(note).trim() } : {}),
  });
  return data;
}

export async function patchAgentLowBalanceWarning(payload: {
  low_balance_warning_enabled?: boolean;
  low_balance_threshold_usd?: number;
}): Promise<{
  success: boolean;
  data?: {
    low_balance_warning_enabled: boolean;
    low_balance_threshold_usd: number;
    low_balance_effective_threshold_usd: number;
  };
  message?: string;
}> {
  const { data } = await client.patch('/agent/settings/low-balance-warning', payload);
  return data;
}

export async function fetchAgentTransactions(params?: {
  page?: number;
  limit?: number;
  owner_scope?: 'self';
  type?: string;
  shop_id?: string;
  period?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
  sort?: string;
  sort_dir?: string;
}): Promise<{
  success: boolean;
  data?: {
    items: AgentWalletLedgerEntry[];
    pagination: AgentTransactionsPagination;
    summary: {
      totalTransactions: number;
      totalDeposits: number;
      totalWithdrawals: number;
      netFlow: number;
    };
  };
  message?: string;
}> {
  const { data } = await client.get('/super-agent/transactions', { params });
  return data;
}

async function getBlobExport(
  url: string,
  params: Record<string, string | number | undefined>,
  fallbackFilename: string,
): Promise<{ blob: Blob; filename: string }> {
  try {
    const response = await client.get<Blob>(url, {
      params,
      responseType: 'blob',
    });
    const disposition = String(response.headers['content-disposition'] || '');
    const match = /filename="([^"]+)"/i.exec(disposition);
    return { blob: response.data, filename: match?.[1] || fallbackFilename };
  } catch (e: unknown) {
    if (axios.isAxiosError(e) && e.response?.data instanceof Blob) {
      try {
        const text = await e.response.data.text();
        const parsed = JSON.parse(text) as { message?: string };
        throw new Error(parsed.message || 'Export failed');
      } catch {
        throw new Error('Export failed');
      }
    }
    throw e;
  }
}

export async function exportSuperAgentWalletTransactions(params: {
  owner_scope?: 'self';
  period?: string;
  date_from?: string;
  date_to?: string;
  type?: string;
  search?: string;
}): Promise<{ blob: Blob; filename: string }> {
  return getBlobExport(
    '/super-agent/transactions/export',
    {
      owner_scope: params.owner_scope,
      period: params.period,
      date_from: params.date_from,
      date_to: params.date_to,
      type: params.type,
      search: params.search,
    },
    `credit-transactions_${params.date_from ?? 'export'}.csv`,
  );
}

export async function exportSuperAgentOnlineBalanceLedger(params: {
  currency?: string;
  type?: string;
  status?: string;
  query?: string;
  dateFrom: string;
  dateTo: string;
}): Promise<{ blob: Blob; filename: string }> {
  return getBlobExport(
    '/super-agent/online-balance/ledger/export',
    {
      currency: params.currency,
      type: params.type,
      status: params.status,
      query: params.query,
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
    },
    `usd-wallet-ledger_${params.dateFrom}_to_${params.dateTo}.csv`,
  );
}

export async function fetchShops(): Promise<{
  success: boolean;
  data?: AgentShop[];
  /** Agent cost rate for this account (0–1). */
  agent_cost_rate?: number;
}> {
  const { data } = await client.get<{
    success: boolean;
    data?: AgentShop[];
    agent_cost_rate?: number;
  }>('/super-agent/shops');
  return data;
}

export async function fetchAgentPlayers(): Promise<{
  success: boolean;
  data?: AgentPlayer[];
}> {
  const { data } = await client.get('/super-agent/players');
  return data;
}

export async function fetchShop(
  shopId: string
): Promise<{ success: boolean; data?: AgentShop; message?: string }> {
  const { data } = await client.get<{ success: boolean; data?: AgentShop; message?: string }>(
    `/super-agent/shops/${shopId}`
  );
  return data;
}

export async function fetchManagedShopCdnTransactions(
  shopId: string,
  params?: {
    page?: number;
    limit?: number;
    type?: string;
    date_from?: string;
    date_to?: string;
  }
): Promise<{
  success: boolean;
  data?: { items: AgentWalletLedgerEntry[]; pagination: AgentTransactionsPagination };
  message?: string;
}> {
  const { data } = await client.get<{
    success: boolean;
    data?: { items: AgentWalletLedgerEntry[]; pagination: AgentTransactionsPagination };
    message?: string;
  }>(`/super-agent/shops/${shopId}/cdn-wallet/transactions`, { params });
  return data;
}

export async function fetchManagedShopCreditTransactionById(
  shopId: string,
  id: string,
): Promise<{ success: boolean; data?: { item: AgentWalletLedgerEntry }; message?: string }> {
  const { data } = await client.get(`/super-agent/shops/${shopId}/cdn-wallet/transactions/${id}`);
  return data;
}

export async function fetchManagedShopUsdLedgerEntryById(
  shopId: string,
  id: string,
): Promise<{ success: boolean; data?: { item: Record<string, unknown> }; message?: string }> {
  const { data } = await client.get(`/super-agent/shops/${shopId}/usd-wallet/ledger/${id}`);
  return data;
}

export async function fetchManagedShopUsdLedger(
  shopId: string,
  params?: { page?: number; limit?: number; type?: string; currency?: string; sort?: string; sort_dir?: string }
): Promise<{ success: boolean; data?: ManagedShopUsdLedgerData; message?: string }> {
  const { data } = await client.get<{ success: boolean; data?: ManagedShopUsdLedgerData; message?: string }>(
    `/super-agent/shops/${shopId}/usd-wallet/ledger`,
    { params }
  );
  return data;
}

export async function createShop(payload: {
  name: string;
  username?: string;
  email: string;
  phone?: string;
  password: string;
  cost_rate?: number;
}): Promise<{ success: boolean; data?: AgentShop; message?: string }> {
  const { data } = await client.post('/agent/shops', payload);
  return data;
}

export async function suspendShop(
  shopId: string
): Promise<{ success: boolean; data?: AgentShop; message?: string }> {
  const { data } = await client.patch(`/super-agent/shops/${shopId}/suspend`);
  return data;
}

export async function unsuspendShop(
  shopId: string
): Promise<{ success: boolean; data?: AgentShop; message?: string }> {
  const { data } = await client.patch(`/super-agent/shops/${shopId}/unsuspend`);
  return data;
}

export async function updateShopCost(
  shopId: string,
  cost_rate: number
): Promise<{ success: boolean; data?: AgentShop; message?: string }> {
  const { data } = await client.patch<{ success: boolean; data?: AgentShop; message?: string }>(
    `/super-agent/shops/${shopId}/rates`,
    { cost_rate }
  );
  return data;
}

export async function updateShopPassword(
  shopId: string,
  password: string
): Promise<{ success: boolean; data?: AgentShop; message?: string }> {
  const { data } = await client.patch<{ success: boolean; data?: AgentShop; message?: string }>(
    `/agent/shops/${shopId}/password`,
    { password }
  );
  return data;
}

export async function updateShopPopupPermission(
  shopId: string,
  can_create_player_popup: boolean
): Promise<{ success: boolean; data?: AgentShop; message?: string }> {
  const { data } = await client.patch<{ success: boolean; data?: AgentShop; message?: string }>(
    `/agent/shops/${shopId}/popup-permissions`,
    { can_create_player_popup }
  );
  return data;
}

export async function purchaseCredits(
  amount: number,
  paymentMethod?: string
): Promise<{ success: boolean; data?: AgentCreditPurchaseData; message?: string }> {
  const body: { amount: number; payment_method?: string } = { amount };
  if (paymentMethod && paymentMethod.trim() !== '') {
    body.payment_method = paymentMethod.trim();
  }
  const { data } = await client.post('/super-agent/credits/purchase', body);
  return data;
}

export async function listZeroxStaticWallets(): Promise<{
  success: boolean;
  data?: { items: ZeroxStaticWallet[] };
  message?: string;
}> {
  const { data } = await client.get('/super-agent/payments/zeroxprocessing/static-wallets');
  return data;
}

export async function initZeroxStaticWallets(currencies: string[]): Promise<{
  success: boolean;
  data?: { items: ZeroxStaticWallet[] };
  message?: string;
}> {
  const { data } = await client.post('/super-agent/payments/zeroxprocessing/static-wallets/init', { currencies });
  return data;
}

export type AdminBroadcastRow = {
  id: string;
  title: string;
  body: string;
  createdAt: string | null;
  acknowledged: boolean;
};

export async function fetchAgentBroadcasts(): Promise<{
  success: boolean;
  data?: { broadcasts: AdminBroadcastRow[] };
}> {
  const { data } = await client.get('/super-agent/broadcasts');
  return data;
}

export async function ackAgentBroadcast(broadcastId: string): Promise<{ success: boolean; message?: string }> {
  const { data } = await client.post(`/super-agent/broadcasts/${broadcastId}/ack`);
  return data;
}

export async function createAgentBroadcast(payload: {
  title: string;
  body: string;
  targetAgentIds?: string[];
  targetShopIds?: string[];
  targetUserIds?: string[];
  expiresAt?: string | null;
}): Promise<{
  success: boolean;
  data?: { id: string; title: string; body: string; audience: string; targetIds: string[]; expiresAt: string | null; createdAt: string | null };
  message?: string;
}> {
  const { data } = await client.post('/super-agent/broadcasts', {
    title: payload.title,
    body: payload.body,
    target_agent_ids: payload.targetAgentIds ?? [],
    target_shop_ids: payload.targetShopIds ?? [],
    target_user_ids: payload.targetUserIds ?? [],
    expires_at: payload.expiresAt ?? undefined,
  });
  return data;
}

export type InAppPopupRow = {
  id: string;
  title: string;
  body: string;
  createdAt: string | null;
  displayMode?: 'once' | 'sticky_daily';
  expiresAt?: string | null;
};

/** Modal in-app popups (separate from bell / broadcasts). */
export async function fetchAgentInAppPopupsUnseen(): Promise<{
  success: boolean;
  data?: { popups: InAppPopupRow[] };
}> {
  const { data } = await client.get('/super-agent/in-app-popups/unseen');
  return data;
}

export async function markAgentInAppPopupSeen(popupId: string): Promise<{ success: boolean; message?: string }> {
  const { data } = await client.post(`/super-agent/in-app-popups/${popupId}/seen`);
  return data;
}

export async function markAgentInAppPopupNeverAgain(popupId: string): Promise<{ success: boolean; message?: string }> {
  const { data } = await client.post(`/super-agent/in-app-popups/${popupId}/never-again`);
  return data;
}

export async function createAgentInAppPopup(payload: {
  title: string;
  body: string;
  targetAgentIds?: string[];
  targetShopIds?: string[];
  targetUserIds?: string[];
  expiresAt?: string | null;
  displayMode?: 'once' | 'sticky_daily';
}): Promise<{
  success: boolean;
  data?: { id: string; title: string; body: string; audience: string; targetIds: string[]; expiresAt: string | null; createdAt: string | null; displayMode?: string };
  message?: string;
}> {
  const { data } = await client.post('/super-agent/in-app-popups', {
    title: payload.title,
    body: payload.body,
    target_agent_ids: payload.targetAgentIds ?? [],
    target_shop_ids: payload.targetShopIds ?? [],
    target_user_ids: payload.targetUserIds ?? [],
    expires_at: payload.expiresAt ?? undefined,
    display_mode: payload.displayMode ?? 'once',
  });
  return data;
}

/** Public catalog (same as kiosk); used to pick allowed games. */
export async function fetchPublicGameProviders(): Promise<{
  success?: boolean;
  data?: { items: Array<{ id: string; name: string; code: string; status?: string }> };
}> {
  const { data } = await client.get('/game-providers');
  return data;
}

export async function fetchManagedAgentGameMappings(agentId: string): Promise<{
  success: boolean;
  data?: { game_ids: string[]; super_agent_game_ids: string[] };
  message?: string;
}> {
  const { data } = await client.get(`/super-agent/agents/${agentId}/game-mappings`);
  return data;
}

export async function putManagedAgentGameMappings(
  agentId: string,
  gameIds: string[],
): Promise<{ success: boolean; message?: string }> {
  const { data } = await client.put(`/super-agent/agents/${agentId}/game-mappings`, { game_ids: gameIds });
  return data;
}

export async function fetchSuperAgentPlayerFraudIndicators(params?: Record<string, unknown>) {
  const { data } = await client.get('/super-agent/player-fraud-indicators', { params });
  return data;
}

export async function fetchSuperAgentPlayerFraudIndicatorDetail(id: string) {
  const { data } = await client.get(`/super-agent/player-fraud-indicators/${encodeURIComponent(id)}`);
  return data;
}

export async function fetchSuperAgentPlayerFraudWithdrawalAnalysis(transactionId: string) {
  const { data } = await client.get(
    `/super-agent/player-fraud-indicators/transaction/${encodeURIComponent(transactionId)}/profit-analysis`,
  );
  return data;
}

export async function resolveSuperAgentPlayerFraudIndicator(id: string, notes?: string) {
  const { data } = await client.post(
    `/super-agent/player-fraud-indicators/${encodeURIComponent(id)}/resolve`,
    { notes: notes || undefined },
  );
  return data;
}
